import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterSunningdale() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-sunningdale",
    name: "Painters Sunningdale",
    location: "Sunningdale, Cape Town",
    rating: 4.7,
    reviews: 92,
    specialties: ["Residential Painting", "Commercial Painting", "Roof Painting", "Waterproofing"],
    image: "https://painterssunningdale.co.za/wp-content/uploads/2024/12/Painters-Sunningdale-1.webp",
    verified: true,
    yearsExperience: 10,
    website: "https://painterssunningdale.co.za",
    email: "info@painterssunningdale.co.za",
    phone: "+27 21 556 3333",
    description: "Painters Sunningdale is a professional painting contractor serving Sunningdale and the West Coast of Cape Town. With over 10 years of experience, we provide comprehensive painting solutions for residential and commercial properties. Our skilled team specializes in roof painting and waterproofing services.",
    services: [
      "Residential Interior Painting",
      "Residential Exterior Painting", 
      "Commercial Painting Services",
      "Roof Painting & Coating",
      "Waterproofing Solutions",
      "Damp Proofing Services",
      "Wall Repairs & Preparation",
      "Texture Coating",
      "Color Consultation",
      "Maintenance Services"
    ],
    serviceAreas: ["Sunningdale", "Table View", "Parklands", "Big Bay", "Bloubergstrand", "West Beach", "Sunset Beach", "West Coast"],
    gallery: [
      "https://painterssunningdale.co.za/wp-content/uploads/2024/12/Painters-Sunningdale-1.webp",
      "https://painterssunningdale.co.za/wp-content/uploads/2024/12/Painters-Sunningdale-2.webp",
      "https://painterssunningdale.co.za/wp-content/uploads/2024/12/Painters-Sunningdale-3.webp",
      "https://painterssunningdale.co.za/wp-content/uploads/2024/12/Painters-Sunningdale-4.webp",
      "https://painterssunningdale.co.za/wp-content/uploads/2024/12/Painters-Sunningdale-5.webp",
      "https://painterssunningdale.co.za/wp-content/uploads/2024/12/Painters-Sunningdale-6.webp"
    ],
    testimonials: [
      {
        name: "Andrew Mitchell",
        rating: 5,
        comment: "Painters Sunningdale did an excellent job on our coastal home. Their roof painting service was outstanding and the team was very professional. Highly recommend their services."
      },
      {
        name: "Karen Smith", 
        rating: 5,
        comment: "We hired Painters Sunningdale for our property and they exceeded expectations. Professional team, quality work, and completed on time. Will definitely use them again."
      },
      {
        name: "Robert Jones",
        rating: 4,
        comment: "Great service and attention to detail. They painted our house and did waterproofing. Very satisfied with the results and would recommend them to others."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Sunningdale | Professional House Painting Services Cape Town | Residential & Commercial";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Sunningdale offers professional painting services in Sunningdale, Cape Town. Specializing in residential, commercial painting, roof painting and waterproofing with over 10 years of experience. Trusted local painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Sunningdale, painters Sunningdale, painting contractors Cape Town, residential painting, commercial painting, roof painting, waterproofing');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${painter.image})` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About {painter.name}</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://painterssunningdale.co.za/wp-content/uploads/2024/12/Painters-Sunningdale-2.webp"
                      alt="Painters Sunningdale professional painting services Cape Town West Coast"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-phone-line mr-3 text-lime-500"></i>
                      <a href={`tel:${painter.phone}`} className="text-gray-700 hover:text-lime-600">
                        {painter.phone}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} with quality materials and expert craftsmanship.</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Project Gallery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews</h2>
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-phone-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Phone</h3>
                      <a href={`tel:${painter.phone}`} className="text-gray-700 hover:text-lime-600">
                        {painter.phone}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and West Coast</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get a Quote</h3>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
